import * as React from 'react';
import { useDiagnosisPermission } from '../../../contexts/DiagnosisPermissionContext';
import { forwardRef, useState, useMemo, useCallback } from 'react';
import {
  Stack,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Button,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { ReadOnlyDataGrid, type ReadOnlyField } from '../../ReadOnlyDataGrid';
import { formatNumberItalian } from '../../../utils/numberFormat';
import { DECIMAL_PLACES } from '../../../constants/decimalPlacesPolicy';
import { ActionButton, ActionSpacer } from '../../common';
import {
  DiagnosisEnergyBalanceReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisIncidenceEneaReadDTO,
  DiagnosisIncidenceEneaCreateDTO,
  IDiagnosisIncidenceEneaCreateDTO,
  DiagnosisIncidenceEneaPercentageReadDTO,
  ConsumptionEnergyVectorFunctionalAreaReadDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';

import { DiagnosisStepHandle } from './Step.types';
import { useDiagnosisEnergyVectorsByDiagnosisId } from '../../../hooks';
import { useDiagnosisEnergyBalanceByDiagnosisId } from '../../../hooks/useDiagnosisEnergyBalance';
import { DiagnosisIncidenceEneaPercentagesDialog } from '../../DiagnosisIncidenceEneaPercentagesDialog';
import { useVectorTypeOptions } from '../../../hooks/useVectorTypeOptions';
import { getFunctionalAreaLabel } from '../../../utils/functionalArea';
import { functionalAreaColors } from '../../../utils/functionalAreaColors';
import { PieChartWrapper, pieArcLabelClasses } from '../../charts';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { formatUnitOfMeasurement } from '../../../utils/unitOfMeasurementFormatter';

// Convert hex color to rgba with opacity
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Generate bright (monitored) and dim (non-monitored) colors from base functional area color
const getMonitoredColor = (baseColor: string): string => {
  // Bright version: increase saturation and lightness
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);

  // Make it brighter: increase each component by 20% (max 255)
  const brightR = Math.min(255, Math.round(r + (255 - r) * 0.2));
  const brightG = Math.min(255, Math.round(g + (255 - g) * 0.2));
  const brightB = Math.min(255, Math.round(b + (255 - b) * 0.2));

  return `#${brightR.toString(16).padStart(2, '0')}${brightG.toString(16).padStart(2, '0')}${brightB.toString(16).padStart(2, '0')}`;
};

const getNonMonitoredColor = (baseColor: string): string => {
  // Dim version: reduce saturation and lightness
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);

  // Make it dimmer: reduce each component by 50% and add gray
  const dimR = Math.max(80, Math.round(r * 0.5));
  const dimG = Math.max(80, Math.round(g * 0.5));
  const dimB = Math.max(80, Math.round(b * 0.5));

  return `#${dimR.toString(16).padStart(2, '0')}${dimG.toString(16).padStart(2, '0')}${dimB.toString(16).padStart(2, '0')}`;
};

interface DiagnosisMonitorEneaStepProps {
  diagnosisId?: string;
}

const getVectorLabel = (
  vector: DiagnosisEnergyVectorsReadDTO,
  vectorTypeOptions: { value: string; label: string }[]
): string => {
  const typeOption = vectorTypeOptions.find(opt => opt.value === vector.vector_type);
  return typeOption ? typeOption.label : vector.code || vector.vector_type || 'Vettore';
};

export const DiagnosisMonitorEneaStep = React.memo(
  forwardRef<DiagnosisStepHandle, DiagnosisMonitorEneaStepProps>(({ diagnosisId }, ref) => {
    const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
    const [selectedTab, setSelectedTab] = useState<string>('main');
    const [percentagesDialogOpen, setPercentagesDialogOpen] = useState(false);
    const queryClient = useQueryClient();
    const theme = useTheme();

    // Espone il metodo save per il ref
    React.useImperativeHandle(ref, () => ({}));

    const { data: diagnosisVectors = [], isLoading: vectorsLoading } =
      useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId!);

    const { data: energyBalanceData = [], isLoading: isLoadingBalance } =
      useDiagnosisEnergyBalanceByDiagnosisId(diagnosisId);

    // Carica tutti i DiagnosisConsumption una volta sola
    const { data: allDiagnosisConsumptions = [] } = useQuery(
      ['all-diagnosis-consumptions', diagnosisId],
      async () => {
        if (!diagnosisId) return [];
        return await apiClient.list_items_diagnosis_consumption__get();
      },
      {
        enabled: !!diagnosisId,
      }
    );

    // Carica DiagnosisIncidenceEnea per la diagnosi
    const { data: diagnosisIncidenceEnea, isLoading: isLoadingIncidence } = useQuery(
      ['diagnosis-incidence-enea', diagnosisId],
      async () => {
        if (!diagnosisId) return null;
        const e =
          await apiClient.search_by_diagnosis_id_diagnosis_incidence_enea_search_by_diagnosis_id__diagnosis_id__get(
            diagnosisId
          );
        return e || null; // Prende il primo (quello di default)
      },
      {
        enabled: !!diagnosisId,
      }
    );

    // Carica le percentuali per la diagnosisIncidenceEnea
    const { data: percentages = [], isLoading: isLoadingPercentages } = useQuery(
      ['diagnosis-incidence-enea-percentages', diagnosisIncidenceEnea?.id],
      async () => {
        if (!diagnosisIncidenceEnea?.id) return [];
        return await apiClient.get_by_incidence_enea_id_diagnosis_incidence_enea_percentage_by_incidence_enea__incidence_enea_id__get(
          diagnosisIncidenceEnea.id
        );
      },
      {
        enabled: !!diagnosisIncidenceEnea?.id,
      }
    );

    // Form state per DiagnosisIncidenceEnea
    const [formData, setFormData] = useState<IDiagnosisIncidenceEneaCreateDTO>({
      code: '',
      unit_of_measurement: '',
      codice_ateco: undefined,
      minimum_percentage_consideration: null as any,
      total_tep_override: undefined,
    });

    // Inizializza formData quando i dati vengono caricati
    React.useEffect(() => {
      if (diagnosisIncidenceEnea) {
        setFormData({
          code: diagnosisIncidenceEnea.code || '',
          unit_of_measurement: diagnosisIncidenceEnea.unit_of_measurement || '',
          codice_ateco: diagnosisIncidenceEnea.codice_ateco || undefined,
          minimum_percentage_consideration:
            diagnosisIncidenceEnea.minimum_percentage_consideration || (null as any),
          total_tep_override: diagnosisIncidenceEnea.total_tep_override ?? undefined,
        });
      }
    }, [diagnosisIncidenceEnea]);

    // Mutation per aggiornare DiagnosisIncidenceEnea
    const updateMutation = useMutation(
      (data: IDiagnosisIncidenceEneaCreateDTO) => {
        if (!diagnosisIncidenceEnea?.id) {
          throw new Error('Incidenza ENEA non trovata');
        }
        return apiClient.update_item_diagnosis_incidence_enea__item_id__put(
          diagnosisIncidenceEnea.id,
          data as any
        );
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['diagnosis-incidence-enea', diagnosisId]);
          queryClient.invalidateQueries(['diagnosis-energy-balance', diagnosisId]);
          toast.success('Incidenza ENEA aggiornata con successo');
          // Ricalcola ConsumptionEnergyVectorFunctionalArea (dipende da Percentuali per consumo totale / total_tep_override)
          recalculateMutation.mutate();
        },
        onError: () => {
          toast.error("Errore durante l'aggiornamento dell'incidenza ENEA");
        },
      }
    );

    const handleSaveIncidence = useCallback(() => {
      // Inviare sempre total_tep_override: null quando vuoto così il backend aggiorna a null
      const payload = {
        ...formData,
        total_tep_override: formData.total_tep_override ?? null,
      };
      updateMutation.mutate(payload as IDiagnosisIncidenceEneaCreateDTO);
    }, [formData, updateMutation]);

    // Mutation per il recalcolo
    const recalculateMutation = useMutation(
      async () => {
        if (!diagnosisId) return;
        // Chiama l'endpoint POST per il recalcolo
        await apiClient.recalculate_for_diagnosis_consumption_energy_vector_functional_area_recalculate_for_diagnosis__diagnosis_id__post(
          diagnosisId
        );
      },
      {
        onSuccess: () => {
          toast.success('Ricalcolo completato con successo');
          // Invalida tutte le query dei vettori energetici
          diagnosisVectors.forEach(vector => {
            if (vector.id) {
              queryClient.invalidateQueries([
                'consumption-energy-vector-functional-area',
                vector.id,
              ]);
            }
          });
          // Invalida anche i dati del bilancio energetico
          queryClient.invalidateQueries(['diagnosis-energy-balance', diagnosisId]);
        },
        onError: () => {
          toast.error('Errore durante il ricalcolo');
        },
      }
    );

    const handleRefresh = useCallback(() => {
      recalculateMutation.mutate();
    }, [recalculateMutation]);

    // Prepara i dati per la tabella principale (bilancio energetico)
    const balanceTableData = useMemo(() => {
      if (!energyBalanceData.length || !diagnosisVectors.length) return [];

      // Crea una mappa vettore id -> vettore per lookup veloce
      const vectorMap = new Map(diagnosisVectors.map(v => [v.id, v]));

      return energyBalanceData.map(balance => {
        const vector = vectorMap.get(balance.diagnosis_energy_vector_id);
        return {
          id: balance.id,
          vettore: vector ? getVectorLabel(vector, vectorTypeOptions) : '-',
          consumo_annuo_tep: balance.tep ? Number(balance.tep) : null,
          incidenza: balance.percentage ? Number(balance.percentage) : null,
          monitoraggio: balance.isToMonitored ? 'Si' : 'No',
        };
      });
    }, [energyBalanceData, diagnosisVectors]);

    // Calcola il totale
    const totalTep = useMemo(() => {
      return balanceTableData.reduce((sum, row) => {
        return sum + (row.consumo_annuo_tep || 0);
      }, 0);
    }, [balanceTableData]);

    const totalPercentage = useMemo(() => {
      return balanceTableData.reduce((sum, row) => {
        return sum + (row.incidenza || 0);
      }, 0);
    }, [balanceTableData]);

    // Riga totale per la tabella principale
    const totalRow = useMemo(() => {
      return {
        vettore: 'TOTALE',
        consumo_annuo_tep: totalTep,
        incidenza: totalPercentage,
        monitoraggio: '',
      };
    }, [totalTep, totalPercentage]);

    const balanceFields: ReadOnlyField[] = [
      {
        field: 'vettore',
        headerName: 'Vettore',
        width: 200,
      },
      {
        field: 'consumo_annuo_tep',
        headerName: 'Consumo Annuo [tep]',
        width: 180,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        decimalPlaces: DECIMAL_PLACES.bilancio_tep,
      },
      {
        field: 'incidenza',
        headerName: 'Incidenza',
        width: 150,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: value => {
          if (value === null || value === undefined) return '-';
          return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale, minimumFractionDigits: 0 })}%`;
        },
      },
      {
        field: 'monitoraggio',
        headerName: 'Monitoraggio',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const value = params.value as string;
          const color =
            value === 'Si'
              ? theme.palette.conditional.success
              : value === 'No'
                ? theme.palette.conditional.error
                : 'inherit';
          return <span style={{ color, fontWeight: 600 }}>{value}</span>;
        },
      },
    ];

    // Tab principale: tabella bilancio energetico
    const renderMainTab = () => {
      return (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Verifica Vettori Energetici
          </Typography>
          <ReadOnlyDataGrid
            fields={balanceFields}
            data={balanceTableData}
            totalRow={totalRow}
            totalRowId="total"
            loading={isLoadingBalance}
            height="auto"
            getRowId={row => row.id}
            showFooter={false}
          />
        </Box>
      );
    };

    // Totale TEP per clusterizzazioni: override se valorizzato, altrimenti calcolato da bilancio
    const effectiveTotalTep = useMemo(() => {
      const override = formData.total_tep_override;
      if (override != null && !Number.isNaN(Number(override))) {
        return Number(override);
      }
      return totalTep;
    }, [formData.total_tep_override, totalTep]);

    // Trova la riga delle percentuali corrispondente al consumo totale (per clusterizzazioni)
    const percentageRowForTotal = useMemo(() => {
      if (!effectiveTotalTep || percentages.length === 0) return null;

      // Trova la riga dove from_value <= effectiveTotalTep e (to_value >= effectiveTotalTep o to_value è null)
      const matchingRows = percentages.filter(p => {
        const fromValue = p.from_value || 0;
        const toValue = p.to_value;
        return (
          fromValue <= effectiveTotalTep &&
          (toValue === null || toValue === undefined || toValue >= effectiveTotalTep)
        );
      });

      if (matchingRows.length === 0) return null;

      // Prendi la riga con il from_value più alto (quella più specifica)
      const bestMatch = matchingRows.reduce((prev, current) => {
        return (prev.from_value || 0) > (current.from_value || 0) ? prev : current;
      });

      // Raggruppa per from_value/to_value e crea la riga con le tre percentuali
      const key = `${bestMatch.from_value}_${bestMatch.to_value ?? 'null'}`;
      const group = percentages.filter(
        p =>
          p.from_value === bestMatch.from_value &&
          (p.to_value === bestMatch.to_value ||
            (p.to_value === null && bestMatch.to_value === null) ||
            (p.to_value === undefined && bestMatch.to_value === undefined))
      );

      const row = {
        from_value: bestMatch.from_value,
        to_value: bestMatch.to_value,
        processo_produttivo: group.find(p => p.functional_area === 'P')?.percentage || 0,
        servizi_ausiliari: group.find(p => p.functional_area === 'A')?.percentage || 0,
        servizi_generali: group.find(p => p.functional_area === 'G')?.percentage || 0,
      };

      return row;
    }, [effectiveTotalTep, percentages]);

    // Tab Incidenza: form per modificare DiagnosisIncidenceEnea
    const renderIncidenceTab = () => {
      return (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Incidenza ENEA
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 600 }}>
            <TextField
              label="Codice"
              value={formData.code}
              onChange={e => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              disabled={!diagnosisIncidenceEnea}
            />
            <TextField
              label="Unità di misura"
              value={formData.unit_of_measurement}
              onChange={e => setFormData({ ...formData, unit_of_measurement: e.target.value })}
              fullWidth
              disabled={!diagnosisIncidenceEnea}
            />
            <TextField
              label="Min. % Considerazione"
              type="number"
              value={formData.minimum_percentage_consideration ?? ''}
              onChange={e =>
                setFormData({
                  ...formData,
                  minimum_percentage_consideration: e.target.value
                    ? Number(e.target.value)
                    : (null as any),
                })
              }
              fullWidth
              disabled={!diagnosisIncidenceEnea}
              helperText="Percentuale minima per considerare un vettore nel monitoraggio"
            />
            <TextField
              label={
                'Consumi totali di energia primaria "equivalenti" del sito ai fini delle clusterizzazioni [tep]'
              }
              type="number"
              value={formData.total_tep_override ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData({
                  ...formData,
                  total_tep_override: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
              fullWidth
              disabled={!diagnosisIncidenceEnea}
              helperText="Se valorizzato, viene usato per la tabella percentuali (clusterizzazioni) al posto del totale calcolato da Consumi e costi. ATTENZIONE: in caso di presenza di cogeneratori i consumi in tep ai fini delle clusterizzazioni non coincidono con i consumi in tep calcolati dal software."
              inputProps={{ min: 0, step: 0.001 }}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleSaveIncidence}
                disabled={!diagnosisIncidenceEnea || updateMutation.isLoading}
              >
                Salva
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  if (diagnosisIncidenceEnea?.id) {
                    setPercentagesDialogOpen(true);
                  } else {
                    toast.error('Incidenza ENEA non trovata');
                  }
                }}
                disabled={!diagnosisIncidenceEnea}
              >
                Gestione Percentuali
              </Button>
            </Stack>
          </Stack>

          {/* Tabella percentuali per consumo totale (usa total_tep_override se presente) */}
          {effectiveTotalTep > 0 && (
            <Box sx={{ mt: 4, maxWidth: 800 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Percentuali per consumo totale:{' '}
                {formatNumberItalian(effectiveTotalTep, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}{' '}
                TEP
              </Typography>
              {isLoadingPercentages ? (
                <Typography variant="body2" color="text.secondary">
                  Caricamento percentuali...
                </Typography>
              ) : percentageRowForTotal ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Consumi [tep] da
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Consumi [tep] a
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Processo Produttivo
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Servizi Ausiliari
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Servizi Generali
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="right">
                          {formatNumberItalian(percentageRowForTotal.from_value)}
                        </TableCell>
                        <TableCell align="right">
                          {percentageRowForTotal.to_value
                            ? formatNumberItalian(percentageRowForTotal.to_value)
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {percentageRowForTotal.processo_produttivo}%
                        </TableCell>
                        <TableCell align="center">
                          {percentageRowForTotal.servizi_ausiliari}%
                        </TableCell>
                        <TableCell align="center">
                          {percentageRowForTotal.servizi_generali}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuna percentuale trovata per il consumo totale di{' '}
                  {formatNumberItalian(effectiveTotalTep, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}{' '}
                  TEP
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    };

    // Componente per la tab del vettore energetico
    const VectorTabContent: React.FC<{ vectorId: string }> = ({ vectorId }) => {
      const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
      const [monitoringMode, setMonitoringMode] = useState<'asis' | 'tobe'>('asis');
      const vector = diagnosisVectors.find(v => v.id === vectorId);

      // Consumo totale del vettore nel periodo di riferimento: stessa fonte usata dal backend
      // per calcolare l'incidenza (diagnosis_consumption.reference_period_quantity), così
      // TOTALE e percentuali risultano coerenti. L'id del vettore è già specifico della diagnosi.
      const totalConsumptionForVector = useMemo(() => {
        if (!vectorId) return null;

        const consumption = allDiagnosisConsumptions.find(
          c => c.diagnosis_energy_vector_id === vectorId && c.type === 'total'
        );

        const quantity = consumption?.reference_period_quantity;
        return quantity != null ? Number(quantity) : null;
      }, [vectorId, allDiagnosisConsumptions]);

      // Carica i dati ConsumptionEnergyVectorFunctionalArea per questo vettore
      const { data: functionalAreaData = [], isLoading: isLoadingFunctionalArea } = useQuery(
        ['consumption-energy-vector-functional-area', vectorId],
        async () => {
          if (!vectorId) return [];
          return await apiClient.get_by_energy_vector_consumption_energy_vector_functional_area_by_energy_vector__energy_vector_id__get(
            vectorId
          );
        },
        {
          enabled: !!vectorId,
        }
      );

      // Calcola i totali
      const totalEstimatedConsumptionIncidence = useMemo(() => {
        return functionalAreaData.reduce((sum, row) => {
          return sum + (Number(row.estimated_consumption_incidence) || 0);
        }, 0);
      }, [functionalAreaData]);

      // Riga totale per la tabella dei vettori energetici
      const totalRowForVector = useMemo(() => {
        return {
          functional_area: 'TOTALE',
          estimated_consumption: totalConsumptionForVector,
          estimated_consumption_incidence: totalEstimatedConsumptionIncidence,
          monitored_consumption_asis: null,
          monitored_consumption_asis_coverage: null,
          enea_coverage: null,
          monitored_consumption_tobe: null,
          monitored_consumption_tobe_coverage: null,
        };
      }, [totalEstimatedConsumptionIncidence, totalConsumptionForVector]);

      // Prepara i dati per il grafico a torta a due strati (deve essere prima di qualsiasi early return)
      const chartData = useMemo(() => {
        if (!functionalAreaData.length) return null;

        // Primo strato: consumo annuo per area funzionale
        const firstLayerData = functionalAreaData.map((row, index) => {
          const consumption = Number(row.estimated_consumption) || 0;
          const area = row.functional_area || '';
          return {
            id: (area || `area-${index}`) as string,
            label: getFunctionalAreaLabel(area),
            value: consumption,
            color: functionalAreaColors[area] || '#cccccc',
          };
        });

        // Calcola il totale del consumo per vedere se c'è un "altro"
        // "Altro" = totale consumo annuo - somma consumi aree funzionali
        const totalConsumption = firstLayerData.reduce((sum, d) => sum + d.value, 0);
        const totalEstimatedConsumption = totalConsumptionForVector || 0;
        const otherConsumption = totalEstimatedConsumption - totalConsumption;
        if (otherConsumption > 0.01) {
          firstLayerData.push({
            id: 'other' as string,
            label: 'Altro',
            value: otherConsumption,
            color: functionalAreaColors.other,
          });
        }

        // Secondo strato: monitorato/non monitorato per area funzionale (valori assoluti)
        const secondLayerData: any[] = [];

        // Calcola i totali monitorati per tutte le aree funzionali
        let totalMonitoredConsumption = 0;
        let totalEstimatedConsumptionAreas = 0;

        functionalAreaData.forEach((row, index) => {
          const estimatedConsumption = Number(row.estimated_consumption) || 0;
          const monitoredConsumption =
            monitoringMode === 'asis'
              ? Number(row.monitored_consumption_asis) || 0
              : Number(row.monitored_consumption_tobe) || 0;
          const nonMonitoredConsumption = estimatedConsumption - monitoredConsumption;

          totalEstimatedConsumptionAreas += estimatedConsumption;
          totalMonitoredConsumption += monitoredConsumption;

          if (estimatedConsumption > 0) {
            const areaColor = functionalAreaColors[row.functional_area] || '#cccccc';

            // Monitorato: colore acceso basato sull'area funzionale
            if (monitoredConsumption > 0.01) {
              secondLayerData.push({
                id: `${row.functional_area}-monitored`,
                label: `${getFunctionalAreaLabel(row.functional_area)} - Monitorato`,
                value: monitoredConsumption,
                color: getMonitoredColor(areaColor),
              });
            }
            // Non monitorato: colore spento basato sull'area funzionale
            if (nonMonitoredConsumption > 0.01) {
              secondLayerData.push({
                id: `${row.functional_area}-non-monitored`,
                label: `${getFunctionalAreaLabel(row.functional_area)} - Non monitorato`,
                value: nonMonitoredConsumption,
                color: getNonMonitoredColor(areaColor),
              });
            }
          }
        });

        // Aggiungi "Altro" monitorato e non monitorato se esiste
        // "Altro" stimato = totale consumo annuo - somma consumi aree funzionali
        // "Altro" monitorato = totale monitorato vettore - somma monitorato aree funzionali
        // Ma non abbiamo il totale monitorato per il vettore, quindi assumiamo che "Altro" non sia monitorato
        // oppure possiamo calcolarlo se abbiamo dati sul totale monitorato
        if (otherConsumption > 0.01) {
          // Per ora, assumiamo che tutto "Altro" sia non monitorato
          // perché non abbiamo dati diretti sul monitoraggio di "Altro"
          const otherMonitoredConsumption = 0;
          const otherNonMonitoredConsumption = otherConsumption - otherMonitoredConsumption;

          if (otherNonMonitoredConsumption > 0.01) {
            const otherColor = functionalAreaColors.other || '#9E9E9E';
            secondLayerData.push({
              id: 'other-non-monitored',
              label: 'Altro - Non monitorato',
              value: otherNonMonitoredConsumption,
              color: getNonMonitoredColor(otherColor),
            });
          }
        }

        return {
          firstLayer: firstLayerData.filter(d => d.value > 0),
          secondLayer: secondLayerData.filter(d => d.value > 0),
        };
      }, [functionalAreaData, monitoringMode, theme, totalConsumptionForVector]);

      if (!vector) return null;

      // Ottieni l'unità di misura del vettore
      const vectorUnitLabel = formatUnitOfMeasurement(vector?.unit_of_measurement || '');

      const functionalAreaFields: ReadOnlyField[] = [
        {
          field: 'functional_area',
          headerName: 'Area Funzionale',
          width: 200,
          valueFormatter: value => getFunctionalAreaLabel(value),
        },
        {
          field: 'estimated_consumption',
          headerName: `Consumo annuo${vectorUnitLabel ? ` [${vectorUnitLabel}]` : ''}`,
          width: 180,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.contatori_variabili_pertinenti,
        },
        {
          field: 'estimated_consumption_incidence',
          headerName: 'Consumo annuo incidenza',
          width: 200,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueFormatter: value => {
            if (value === null || value === undefined) return '-';
            return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale, minimumFractionDigits: 0 })}%`;
          },
        },
        {
          field: 'monitored_consumption_asis',
          headerName: `Monitorato AS IS${vectorUnitLabel ? ` [${vectorUnitLabel}]` : ''}`,
          width: 200,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.contatori_variabili_pertinenti,
        },
        {
          field: 'monitored_consumption_asis_coverage',
          headerName: 'Monitorato AS IS copertura',
          width: 200,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueFormatter: value => {
            if (value === null || value === undefined) return '-';
            return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale, minimumFractionDigits: 0 })}%`;
          },
          renderCell: params => {
            const coverage =
              params.value !== null && params.value !== undefined ? Number(params.value) : null;
            const eneaCoverage =
              params.row.enea_coverage !== null && params.row.enea_coverage !== undefined
                ? Number(params.row.enea_coverage)
                : null;

            if (coverage === null || eneaCoverage === null) {
              return <span>-</span>;
            }

            // Calcola la percentuale rispetto a enea_coverage
            const percentage = (coverage / eneaCoverage) * 100;

            let color: string;
            if (percentage < 70) {
              color = theme.palette.conditional.error;
            } else if (percentage < 100) {
              color = theme.palette.conditional.warning;
            } else {
              color = theme.palette.conditional.success;
            }

            return (
              <span style={{ color, fontWeight: 600 }}>
                {formatNumberItalian(coverage, {
                  maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale,
                })}
                %
              </span>
            );
          },
        },
        {
          field: 'enea_coverage',
          headerName: 'Copertura ENEA',
          width: 150,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueFormatter: value => {
            if (value === null || value === undefined) return '-';
            return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale, minimumFractionDigits: 0 })}%`;
          },
        },
        {
          field: 'monitored_consumption_tobe',
          headerName: `Monitorato TO BE${vectorUnitLabel ? ` [${vectorUnitLabel}]` : ''}`,
          width: 200,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          decimalPlaces: DECIMAL_PLACES.contatori_variabili_pertinenti,
        },
        {
          field: 'monitored_consumption_tobe_coverage',
          headerName: 'Monitorato TO BE copertura',
          width: 200,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          valueFormatter: value => {
            if (value === null || value === undefined) return '-';
            return `${formatNumberItalian(value, { maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale, minimumFractionDigits: 0 })}%`;
          },
          renderCell: params => {
            const coverage =
              params.value !== null && params.value !== undefined ? Number(params.value) : null;
            const eneaCoverage =
              params.row.enea_coverage !== null && params.row.enea_coverage !== undefined
                ? Number(params.row.enea_coverage)
                : null;

            if (coverage === null || eneaCoverage === null) {
              return <span>-</span>;
            }

            // Calcola la percentuale rispetto a enea_coverage
            const percentage = (coverage / eneaCoverage) * 100;

            let color: string;
            if (percentage < 70) {
              color = theme.palette.conditional.error;
            } else if (percentage < 100) {
              color = theme.palette.conditional.warning;
            } else {
              color = theme.palette.conditional.success;
            }

            return (
              <span style={{ color, fontWeight: 600 }}>
                {formatNumberItalian(coverage, {
                  maximumFractionDigits: DECIMAL_PLACES.bilancio_percentuale,
                })}
                %
              </span>
            );
          },
        },
      ];

      const handleViewModeChange = (
        _: React.MouseEvent<HTMLElement>,
        newMode: 'table' | 'chart' | null
      ) => {
        if (newMode) {
          setViewMode(newMode);
        }
      };

      const handleMonitoringModeChange = (
        _: React.MouseEvent<HTMLElement>,
        newMode: 'asis' | 'tobe' | null
      ) => {
        if (newMode) {
          setMonitoringMode(newMode);
        }
      };

      // Calcola i raggi per il grafico a ciambella a due strati
      // Primo strato (interno): consumo annuo per area funzionale (con buco al centro)
      // Secondo strato (esterno): monitorato/non monitorato (più fine)
      // Dimensioni aumentate del 50%
      const firstLayerInnerRadius = 60; // Buco al centro (40 * 1.5)
      const firstLayerOuterRadius = 180; // 120 * 1.5
      const secondLayerInnerRadius = 180; // 120 * 1.5
      const secondLayerOuterRadius = 225; // 150 * 1.5

      return (
        <Box>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">{getVectorLabel(vector, vectorTypeOptions)}</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {viewMode === 'chart' && (
                <ToggleButtonGroup
                  value={monitoringMode}
                  exclusive
                  onChange={handleMonitoringModeChange}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="asis">AS IS</ToggleButton>
                  <ToggleButton value="tobe">TO BE</ToggleButton>
                </ToggleButtonGroup>
              )}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                color="primary"
              >
                <ToggleButton value="table" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TableRowsIcon fontSize="small" />
                  <Typography variant="body2">Tabella</Typography>
                </ToggleButton>
                <ToggleButton value="chart" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <ShowChartIcon fontSize="small" />
                  <Typography variant="body2">Grafico</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>

          {viewMode === 'table' ? (
            <ReadOnlyDataGrid
              fields={functionalAreaFields}
              data={functionalAreaData}
              totalRow={totalRowForVector}
              totalRowId="total"
              loading={isLoadingFunctionalArea}
              height="auto"
              getRowId={row => row.id}
              showFooter={false}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 500,
              }}
            >
              {chartData && chartData.firstLayer.length > 0 ? (
                <PieChartWrapper
                  width={1500}
                  height={750}
                  unitOfMeasurement={vectorUnitLabel}
                  slotProps={{
                    legend: {
                      direction: 'column',
                      position: { vertical: 'middle', horizontal: 'right' },
                    },
                  }}
                  margin={{ top: 30, right: 750, left: 30, bottom: 30 }}
                  series={[
                    {
                      innerRadius: firstLayerInnerRadius,
                      outerRadius: firstLayerOuterRadius,
                      data: chartData.firstLayer,
                      arcLabel: item => ``,
                      valueFormatter: ({ value }) =>
                        formatNumberItalian(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
                      highlightScope: { fade: 'global', highlight: 'item' },
                      highlighted: { additionalRadius: 2 },
                      cornerRadius: 0,
                    },
                    {
                      innerRadius: secondLayerInnerRadius,
                      outerRadius: secondLayerOuterRadius,
                      data: chartData.secondLayer,
                      arcLabel: item => '',
                      valueFormatter: ({ value }) =>
                        formatNumberItalian(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
                      highlightScope: { fade: 'global', highlight: 'item' },
                      highlighted: { additionalRadius: 2 },
                      cornerRadius: 0,
                    },
                  ]}
                  sx={{
                    [`& .${pieArcLabelClasses.root}`]: {
                      fontSize: '12px',
                    },
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessun dato disponibile per il grafico
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    };

    // Tab per vettore energetico
    const renderVectorTab = (vectorId: string) => {
      return <VectorTabContent vectorId={vectorId} />;
    };

    // Determina le tab disponibili
    const tabs = useMemo(() => {
      const tabList: Array<{ value: string; label: string }> = [
        { value: 'main', label: 'Informazioni generali' },
        { value: 'incidence', label: 'Incidenza' },
      ];

      // Aggiungi tab per ogni vettore energetico
      diagnosisVectors.forEach(vector => {
        if (vector.id) {
          tabList.push({
            value: vector.id,
            label: getVectorLabel(vector, vectorTypeOptions),
          });
        }
      });

      return tabList;
    }, [diagnosisVectors, vectorTypeOptions]);

    // Seleziona la prima tab disponibile se quella selezionata non esiste più
    React.useEffect(() => {
      if (tabs.length > 0 && !tabs.find(t => t.value === selectedTab)) {
        setSelectedTab(tabs[0].value);
      }
    }, [tabs, selectedTab]);

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        {vectorsLoading || isLoadingIncidence ? (
          <Typography variant="body2" color="text.secondary">
            Caricamento dati...
          </Typography>
        ) : (
          <>
            <Stack
              direction="row"
              spacing="1rem"
              alignItems="center"
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing="1rem" alignItems="center" flexWrap="wrap">
                <Typography variant="h6">Monitoraggio ENEA</Typography>
                <Tabs
                  value={selectedTab}
                  onChange={(_, value) => setSelectedTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ minHeight: 40 }}
                >
                  {tabs.map(tab => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={tab.label}
                      sx={{ textTransform: 'none' }}
                    />
                  ))}
                </Tabs>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={recalculateMutation.isLoading}
              >
                Aggiorna
              </Button>
            </Stack>

            {selectedTab === 'main' && renderMainTab()}
            {selectedTab === 'incidence' && renderIncidenceTab()}
            {selectedTab !== 'main' && selectedTab !== 'incidence' && renderVectorTab(selectedTab)}
          </>
        )}

        {/* Modale percentuali */}
        {diagnosisIncidenceEnea?.id && (
          <DiagnosisIncidenceEneaPercentagesDialog
            open={percentagesDialogOpen}
            diagnosisIncidenceEneaId={diagnosisIncidenceEnea.id}
            onClose={() => setPercentagesDialogOpen(false)}
          />
        )}
        <ActionSpacer></ActionSpacer>
      </Stack>
    );
  })
);

DiagnosisMonitorEneaStep.displayName = 'DiagnosisMonitorEneaStep';
